<template>
  <div>
    <MainHeader title="Data Deletion">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'Legal' })"
        />
      </template>
    </MainHeader>

    <div class="view legal-doc-view">
      <div class="wrapper-card legal-doc-card">
        <div class="legal-doc-header">
          <h1 class="legal-doc-title">Data Retention and Deletion Policy</h1>
          <div class="legal-doc-dates">
            <span class="legal-date-badge">Effective: 04/09/2025</span>
            <span class="legal-date-badge">Updated: 05/19/2025</span>
          </div>
        </div>

        <p class="legal-body">
          At Lucky Friday, we believe your data belongs to you. This Data
          Retention and Deletion Policy explains how long we keep your personal
          information and financial data, and how you can request deletion at
          any time.
        </p>

        <div class="legal-section">
          <h3 class="legal-section-title">1. Data We Collect and Retain</h3>
          <ul class="legal-list">
            <li>
              <strong>Account Information</strong> (e.g., name, email address,
              preferences)<br /><em>Retention:</em> Retained as long as your
              account is active.
            </li>
            <li>
              <strong>Financial Data</strong> (e.g., connected accounts,
              transaction history, budgets)<br /><em>Retention:</em> Retained
              for the life of your account. You may delete this at any time from
              within the app.
            </li>
            <li>
              <strong>Autocategorization Rules and Custom Settings</strong
              ><br /><em>Retention:</em> Persisted until manually deleted by you
              or upon account deletion.
            </li>
            <li>
              <strong>Analytics Data</strong> (e.g., anonymized usage data from
              Plausible Analytics)<br /><em>Retention:</em> Only aggregated,
              non-identifiable data is retained, per Plausible's privacy-focused
              design.
            </li>
          </ul>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">2. How Long We Keep Your Data</h3>
          <p class="legal-body">
            We retain your personal and financial data only for as long as
            necessary to:
          </p>
          <ul class="legal-list">
            <li>Provide you with the services you request</li>
            <li>Comply with legal and regulatory obligations</li>
            <li>Improve the reliability and safety of the app</li>
            <li>Resolve disputes and enforce our Terms of Service</li>
          </ul>
          <p class="legal-body">
            If you delete your account, your data is permanently deleted within
            30 days, unless retention is required by law.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">3. How to Request Data Deletion</h3>
          <p class="legal-body">
            You can delete your data in one of the following ways:
          </p>
          <ul class="legal-list">
            <li>
              <strong>By Email:</strong><br />
              Email us at
              <a href="mailto:support@luckyfridayapp.com" class="legal-link"
                >support@luckyfridayapp.com</a
              >
              from your registered address with the subject line "Delete My
              Account."
            </li>
          </ul>
          <p class="legal-body">
            Once your request is confirmed, all personally identifiable
            information and stored data will be deleted from our systems within
            30 days.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">4. Exceptions</h3>
          <p class="legal-body">We may retain certain limited data for:</p>
          <ul class="legal-list">
            <li>Legal compliance (e.g., financial audit logs, tax records)</li>
            <li>Fraud prevention and security monitoring</li>
            <li>
              Backup integrity (backups are overwritten on a rolling basis,
              typically within 30 days)
            </li>
          </ul>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">5. Children's Data</h3>
          <p class="legal-body">
            Lucky Friday is not intended for users under 18. We do not knowingly
            collect data from minors. If we become aware that a user under 18
            has provided personal data, we will promptly delete such
            information.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">6. Changes to This Policy</h3>
          <p class="legal-body">
            We may update this policy periodically. We will notify users via the
            app or email for any material changes. Continued use of Lucky Friday
            after changes means you accept the updated policy.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">Contact Us</h3>
          <p class="legal-body">
            If you have any questions about this policy or your data, contact us
            at:
          </p>
          <a
            href="mailto:support@luckyfridayapp.com"
            class="legal-contact-link"
          >
            <font-awesome-icon
              :icon="['fas', 'envelope']"
              style="margin-right: 6px"
            />
            support@luckyfridayapp.com
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '../../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'DataDeletion',
  components: { MainHeader },
  setup() {
    const router = useRouter();
    return { router };
  },
};
</script>

<style scoped>
.legal-doc-view {
  max-width: 600px;
  margin: 0 auto;
}

.legal-doc-card {
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
  padding: 1.5rem;
}

.legal-doc-header {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1.5px solid #eeedfe;
}
.dark-theme .legal-doc-header {
  border-bottom-color: #2a3a4e;
}

.legal-doc-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.6rem;
  letter-spacing: -0.01em;
  line-height: 1.3;
}
.dark-theme .legal-doc-title {
  color: white;
}

.legal-doc-dates {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.legal-date-badge {
  font-size: 0.7rem;
  font-weight: 600;
  background: var(--purple-50);
  color: var(--color-accent-purple);
  padding: 2px 10px;
  border-radius: 20px;
}
.dark-theme .legal-date-badge {
  background: #1e1a40;
}

.legal-section {
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f0f0f0;
}
.dark-theme .legal-section {
  border-bottom-color: #2a3a4e;
}
.legal-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.legal-section-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--color-accent-purple);
  margin: 0 0 0.6rem;
}
.dark-theme .legal-section-title {
  color: var(--color-accent-teal);
}

.legal-body {
  font-size: 0.82rem;
  color: #515a65;
  line-height: 1.65;
  margin: 0 0 0.6rem;
}
.dark-theme .legal-body {
  color: #d4d2d2;
}

.legal-list {
  font-size: 0.82rem;
  color: #515a65;
  line-height: 1.65;
  padding-left: 1.25rem;
  margin: 0.4rem 0;
}
.dark-theme .legal-list {
  color: #d4d2d2;
}
.legal-list li {
  margin-bottom: 0.5rem;
}
.legal-list li:last-child {
  margin-bottom: 0;
}

.legal-link {
  color: var(--color-accent-purple);
  font-weight: 500;
  text-decoration: none;
}
.legal-link:hover {
  text-decoration: underline;
}
.dark-theme .legal-link {
  color: var(--color-accent-teal);
}

.legal-contact-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-accent-purple);
  text-decoration: none;
  background: var(--purple-50);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-pill);
  transition: background var(--transition-fast);
}
.legal-contact-link:hover {
  background: var(--purple-100);
}
.dark-theme .legal-contact-link {
  background: #1e1a40;
  color: var(--color-accent-teal);
}
</style>

<!-- <template>
  <div>
    <MainHeader title="Data Deletion">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'Legal' })"
        />
      </template>
    </MainHeader>

    <div class="view support-page">
      <div class="wrapper-card closing-text ps-2 pe-2 small-grey-text">
        <div>
          <h3>Data Retention and Deletion Policy</h3>
          <h4>Effective Date: 04/09/2025</h4>
          <h4>Last Updated: 05/19/2025</h4>

          <p>
            At Lucky Friday, we believe your data belongs to you. This Data
            Retention and Deletion Policy explains how long we keep your
            personal information and financial data, and how you can request
            deletion at any time.
          </p>

          <h4>1. Data We Collect and Retain</h4>
          <ul>
            <li>
              <strong>Account Information</strong> (e.g., name, email address,
              preferences)<br />
              <em>Retention:</em> Retained as long as your account is active.
            </li>
            <li>
              <strong>Financial Data</strong> (e.g., connected accounts,
              transaction history, budgets)<br />
              <em>Retention:</em> Retained for the life of your account. You may
              delete this at any time from within the app.
            </li>
            <li>
              <strong>Autocategorization Rules and Custom Settings</strong
              ><br />
              <em>Retention:</em> Persisted until manually deleted by you or
              upon account deletion.
            </li>
            <li>
              <strong>Analytics Data</strong> (e.g., anonymized usage data from
              Plausible Analytics)<br />
              <em>Retention:</em> Only aggregated, non-identifiable data is
              retained, per Plausible’s privacy-focused design.
            </li>
          </ul>

          <h4>2. How Long We Keep Your Data</h4>
          <p>
            We retain your personal and financial data only for as long as
            necessary to:
          </p>
          <ul>
            <li>Provide you with the services you request</li>
            <li>Comply with legal and regulatory obligations</li>
            <li>Improve the reliability and safety of the app</li>
            <li>Resolve disputes and enforce our Terms of Service</li>
          </ul>
          <p>
            If you delete your account, your data is permanently deleted within
            30 days, unless retention is required by law.
          </p>

          <h4>3. How to Request Data Deletion</h4>
          <p>You can delete your data in one of the following ways:</p>
          <ul>
            <li>
              <strong>By Email:</strong><br />
              Email us at
              <a href="mailto:support@luckyfridayapp.com"
                >support@luckyfridayapp.com</a
              >
              from your registered address with the subject line “Delete My
              Account.”
            </li>
          </ul>
          <p>
            Once your request is confirmed, all personally identifiable
            information and stored data will be deleted from our systems within
            30 days.
          </p>

          <h4>4. Exceptions</h4>
          <p>We may retain certain limited data for:</p>
          <ul>
            <li>Legal compliance (e.g., financial audit logs, tax records)</li>
            <li>Fraud prevention and security monitoring</li>
            <li>
              Backup integrity (backups are overwritten on a rolling basis,
              typically within 30 days)
            </li>
          </ul>

          <h4>5. Children’s Data</h4>
          <p>
            Lucky Friday is not intended for users under 18. We do not knowingly
            collect data from minors. If we become aware that a user under 18
            has provided personal data, we will promptly delete such
            information.
          </p>

          <h4>6. Changes to This Policy</h4>
          <p>
            We may update this policy periodically. We will notify users via the
            app or email for any material changes. Continued use of Lucky Friday
            after changes means you accept the updated policy.
          </p>

          <h4>Contact Us</h4>
          <p>
            If you have any questions about this policy or your data, contact us
            at:
          </p>
          <p>
            📧
            <a href="mailto:support@luckyfridayapp.com">support@luckyfridayapp.com</a>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '../../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'User-Support',
  components: {
    MainHeader,
  },
  setup() {
    const router = useRouter();

    return {
      router,
    };
  },
};
</script>
<style scoped>
a {
  color: black;
}
</style> -->
